import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Lineicons } from '@lineiconshq/react-lineicons';
import { XmarkStroke } from '@lineiconshq/free-icons';
import React from 'react';
import { Dialog, DialogOverlay, DialogPortal, DialogTitle } from '@/components/ui/dialog';
import { AritoFormV2, BottomActionBarV2, AritoIcon } from '@/components/custom/erp';
import { useDialogStack } from '@/hooks/use-dialog-stack';
import { BasicInfo, DetailsTab, OtherTab } from './tabs';
import { initialValues, searchSchema } from '../schema';
import { useSearchFieldStates } from '../hooks';

interface InitialSearchDialogProps {
  open: boolean;
  onClose: () => void;
  onSearch: (values: BanHangPayload) => void;
  setDates?: (fromDate: string, toDate: string) => void;
}

export const InitialSearchDialog: React.FC<InitialSearchDialogProps> = ({ open, onClose, onSearch, setDates }) => {
  const { contentZIndex, overlayZIndex } = useDialogStack(open);
  const searchFieldStates = useSearchFieldStates();

  const handleSubmit = (data: BanHangPayload) => {
    const combinedData = {
      ...data,
      tai_khoan_no: searchFieldStates.account?.uuid || '',
      ma_khach_hang: searchFieldStates.customer?.uuid || '',
      nhom_khach_hang_1: searchFieldStates.customerGroup1?.uuid || '',
      nhom_khach_hang_2: searchFieldStates.customerGroup2?.uuid || '',
      nhom_khach_hang_3: searchFieldStates.customerGroup3?.uuid || '',
      khu_vuc: searchFieldStates.region?.uuid || ''
    };

    setDates?.(combinedData.ngay_h_toan_tu, combinedData.ngay_h_toan_den);
    onSearch(combinedData);
  };

  return (
    <Dialog modal={true} open={open} onOpenChange={onClose}>
      <DialogPortal>
        <DialogOverlay className='fixed inset-0 bg-black/50' style={{ zIndex: overlayZIndex }} />
        <div className='fixed inset-0 flex items-center justify-center p-4' style={{ zIndex: contentZIndex }}>
          <DialogPrimitive.Content
            onOpenAutoFocus={event => event.preventDefault()}
            onCloseAutoFocus={event => event.preventDefault()}
            onInteractOutside={event => event.preventDefault()}
            onPointerDownOutside={event => event.preventDefault()}
            onFocusOutside={event => event.preventDefault()}
            className='flex max-h-[85vh] w-[820px] flex-col overflow-hidden border border-[#cfd8e3] bg-white shadow-lg outline-none'
          >
            <div className='flex h-11 min-h-11 items-center justify-between border-b border-[#d1d5db] bg-[#f2f7fc] pl-3.5 pr-4'>
              <DialogTitle className='flex items-center text-[16px] font-bold text-black'>
                <AritoIcon icon={12} className='mx-2' />
                <span>Tất toán hóa đơn</span>
              </DialogTitle>
              <button type='button' onClick={onClose} className='inline-flex size-8 items-center justify-center'>
                <Lineicons aria-hidden='true' icon={XmarkStroke} className='size-[18px]' />
              </button>
            </div>
            <AritoFormV2
              mode='add'
              hasAritoActionBar={false}
              schema={searchSchema}
              initialData={initialValues}
              onSubmit={handleSubmit}
              className='w-full'
              headerFields={
                <div className='max-h-[calc(100vh-250px)] w-[800px] min-w-[800px] overflow-y-auto'>
                  <BasicInfo
                    searchFieldStates={{ account: searchFieldStates.account, setAccount: searchFieldStates.setAccount }}
                  />
                </div>
              }
              tabs={[
                {
                  id: 'details',
                  label: 'Chi tiết',
                  component: (
                    <DetailsTab
                      searchFieldStates={{
                        customer: searchFieldStates.customer,
                        setCustomer: searchFieldStates.setCustomer,
                        customerGroup1: searchFieldStates.customerGroup1,
                        setCustomerGroup1: searchFieldStates.setCustomerGroup1,
                        customerGroup2: searchFieldStates.customerGroup2,
                        setCustomerGroup2: searchFieldStates.setCustomerGroup2,
                        customerGroup3: searchFieldStates.customerGroup3,
                        setCustomerGroup3: searchFieldStates.setCustomerGroup3,
                        region: searchFieldStates.region,
                        setRegion: searchFieldStates.setRegion
                      }}
                    />
                  )
                },
                {
                  id: 'other',
                  label: 'Khác',
                  component: <OtherTab />
                }
              ]}
              bottomBar={<BottomActionBarV2 mode='search' onClose={onClose} submitTitle='Đồng ý' />}
            />
          </DialogPrimitive.Content>
        </div>
      </DialogPortal>
    </Dialog>
  );
};

export default InitialSearchDialog;
